import React, { useMemo, useState } from "react";
import { PayeeAccountVM, PayeeAccountTypesEnum } from "@propertelligent/client-api";
import { GetImages } from "../../../utils/GetImages";
import ActionMenu from "../../../newComponents/actionMenus/action-menu";
import dropdownIcon from "../../../assets/componentsIcons/dropdown/dropdown.svg";
import editIcon from "../../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../../assets/componentsIcons/dropdown/delete.svg";
import { ConfirmationPopup } from "../../../newComponents/confirmation-popup/confirmation-popup-demo";
import useInfiniteScroll from "react-infinite-scroll-hook";
import SimpleSpinner from "../../../components/loader";
import EmptyState from "../../../newComponents/emptystate";
import bluePayeeIcon from "../../../assets/paymentAccountIcon.svg";
import apiHelper from "../../../utils/apiHelper";
import { PaymentService } from "@propertelligent/client-api";
import { usePermissions } from "../../../hooks/usePermissions";
import { runFullRefresh } from "../../../hooks/useAggregateFilterCounts";

interface DataHookState {
  rows: PayeeAccountVM[];
  loading: boolean;
  hasMore: boolean;
  fetchData: (page: number) => void;
  currentPage: number;
  handleFullRefresh?: () => void;
}

interface PayeeAccountCardItemProps {
  className?: string;
  dataHookState: DataHookState;
  onEditPayeeAccount?: (payeeAccount: PayeeAccountVM) => void;
  hasAnyPayeeAccounts?: boolean;
}

const PayeeAccountCardItem = ({
  className = "",
  dataHookState,
  onEditPayeeAccount,
  hasAnyPayeeAccounts = true,
}: PayeeAccountCardItemProps) => {
  const { rows, loading, hasMore, fetchData, currentPage, handleFullRefresh } = dataHookState;
  const [showConfirmation, setShowConfirmation] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const check = usePermissions();
  const canWrite = check.canWrite("PayeeAccounts");

  const menuItemsMap = useMemo(() => {
    return rows.reduce((acc, listingData) => {
      const items = [];
      if (canWrite) {
        items.push(
          {
            icon: editIcon,
            label: "Edit",
            action: () => onEditPayeeAccount?.(listingData),
            isDivider: false,
            isDestructive: false,
          },
          { isDivider: true },
          {
            icon: deleteIcon,
            label: "Delete",
            action: () => setShowConfirmation(String(listingData.id)),
            isDivider: false,
            isDestructive: true,
          }
        );
      }
      acc[listingData.id || 0] = items;
      return acc;
    }, {} as Record<string | number, any[]>);
  }, [rows, onEditPayeeAccount, canWrite]);

  const loadMoreRecords = () => {
    if (!loading && hasMore) {
      fetchData(currentPage + 1);
    }
  };

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: loadMoreRecords,
    disabled: !hasMore,
    rootMargin: "0px 0px 100px 0px",
  });

  const handleDeletePayeeAccount = (payeeAccountId: any) => {
    if (isDeleting) return;

    setIsDeleting(true);
    apiHelper(PaymentService.deleteApiPayeeAccount, {
      title: "Payee Account Deleted",
      successMessage: "Payee account deleted successfully",
      showNotification: true,
    }, payeeAccountId)
      .then((res) => {
        if (res?.data?.success) {
          runFullRefresh(handleFullRefresh, fetchData);
          setShowConfirmation(null);
        }
      })
      .catch((err) => {
        console.error("Error deleting payee account:", err);
        setShowConfirmation(null);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  return (
    <div className="infinite-scroll-container">
      {loading && rows.length === 0 ? (
        <div className="d-flex justify-content-center align-items-center flex-grow-1 h-100 w-100">
          <SimpleSpinner myClassName="Icon50" />
        </div>
      ) : rows.length === 0 && !loading ? (
        <EmptyState
          icon={bluePayeeIcon}
          heading={hasAnyPayeeAccounts ? "No Payee Accounts Found" : "Add Your First Payee Account"}
          buttonLabel={hasAnyPayeeAccounts ? "Add Your Payee Account" : "Add Payee Account"}
          isButton={hasAnyPayeeAccounts ? false : true}
          onButtonClick={() => onEditPayeeAccount?.({} as PayeeAccountVM)}
        />
      ) : (
        <>
          {rows.map((listingData) => (
            <div key={listingData.id} className={`listing-card ${className}`}>
              <div className="listing-card-header">

                <div className="listing-image-container payment-account-box">
                  <img
                    src={GetImages.payeeAccountIcon}
                    alt="Payment Method"
                    width={32}
                    height={32}
                    className="listing-image color-white-icon"
                  />
                </div>
                <div className="listing-details">
                  <div className="listing-info-container">
                    <div className="listing-info">
                      <h3 className="listing-address" title={listingData.accountName}>
                        {listingData.accountName}
                      </h3>
                      <p className="listing-type">
                        {listingData?.formatted?.PayeeAccountTypeId}
                      </p>
                    </div>
                  </div>
                </div>
                {canWrite && menuItemsMap[listingData.id || 0]?.length > 0 && (
                  <ActionMenu
                    trigger={<img src={dropdownIcon} alt="Menu" width={24} height={24} className="iconStyleWithWhiteFilter" />}
                    menuItems={menuItemsMap[listingData.id || 0]}
                    Id={listingData.id || 0}
                  />
                )}
              </div>

              {showConfirmation === String(listingData.id) && (
                <ConfirmationPopup
                  isOpen={true}
                  title="Delete Payee Account"
                  message={`Do you want to delete the  <strong>${listingData.accountName}</strong> account?`}
                  primaryButtonLabel="Delete"
                  secondaryButtonLabel="Cancel"
                  onPrimaryAction={() => handleDeletePayeeAccount(listingData.id)}
                  onSecondaryAction={() => setShowConfirmation(null)}
                  onClose={() => setShowConfirmation(null)}
                  isPrimaryDestructive={true}
                  isLoading={isDeleting}
                  loadingLabel="Deleting..."
                />
              )}
            </div>
          ))}
          {(loading || hasMore) && (
            <div ref={sentryRef} className="loading-trigger">
              {loading && (
                <SimpleSpinner myClassName="Icon40" />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PayeeAccountCardItem;